import { Box, TextField, TextFieldProps } from "@mui/material";
import { makeSxStyles } from "ui";
import { countCharacters } from "@/shared/utils/character-count";

type CharacterLimitTextFieldProps = Omit<
  TextFieldProps,
  "value" | "helperText"
> & {
  value: string;
  maxLength: number;
  /** Validation message shown on the left (e.g. "Headline is required"). */
  errorMessage?: string | false;
};

// A TextField with a right-aligned "n/max" counter under it (ESR-1655).
// Typing past the limit is allowed on purpose: the counter and border turn
// red instead, so a pasted value isn't silently truncated.
const CharacterLimitTextField = ({
  value,
  maxLength,
  errorMessage,
  error,
  ...props
}: CharacterLimitTextFieldProps) => {
  const styles = useStyles();
  const characterCount = countCharacters(value);
  const isOverLimit = characterCount > maxLength;
  const showCounter = value.length > 0;
  const hasError = Boolean(error) || isOverLimit;

  return (
    <TextField
      {...props}
      value={value}
      error={hasError}
      helperText={
        (errorMessage || showCounter) && (
          <Box component="span" sx={styles.helper}>
            <span>{errorMessage}</span>
            {showCounter && (
              <span>
                {characterCount}/{maxLength}
              </span>
            )}
          </Box>
        )
      }
      FormHelperTextProps={{ sx: styles.helperRoot }}
    />
  );
};

const useStyles = makeSxStyles((theme) => ({
  helperRoot: {
    // The theme positions helper text absolutely under the input; the counter
    // needs to take up space so it doesn't overlap the next row.
    position: "static",
    mx: 0,
    mt: "4px",
    color: theme.palette.grey[100],
    // MUI adds .Mui-error while `error` is set (required or over the limit),
    // so the message and the counter turn red along with the border.
    "&.Mui-error": {
      color: theme.palette.error.main,
    },
  },
  helper: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    pr: "20px",
  },
}));

export default CharacterLimitTextField;
